/**
 * Line-block helpers for textarea toolbar (bullets) while keeping plain text for APIs.
 */

/**
 * @param {string} text
 * @param {number} pos
 */
function lineBoundsAt(text, pos) {
  const safe = Math.max(0, Math.min(pos, text.length));
  const lineStart = text.lastIndexOf("\n", safe - 1) + 1;
  const nl = text.indexOf("\n", lineStart);
  const lineEnd = nl === -1 ? text.length : nl;
  return { lineStart, lineEnd };
}

/**
 * Full line block from selection (inclusive of every line that touches the range).
 * @param {string} text
 * @param {number} selStart
 * @param {number} selEnd
 * @returns {{ blockStart: number, blockEnd: number }}
 */
export function getSelectedLineBlockBounds(text, selStart, selEnd) {
  const a = Math.min(selStart, selEnd);
  const b = Math.max(selStart, selEnd);
  const first = lineBoundsAt(text, a);
  const last = lineBoundsAt(text, Math.max(a, b - 1));
  return { blockStart: first.lineStart, blockEnd: last.lineEnd };
}

/**
 * @param {string} line
 */
function lineToggleBullet(line) {
  const m = /^(\s*)(.*)$/.exec(line);
  const ind = m ? m[1] : "";
  let rest = m ? m[2] : line;
  if (/^[-*•]\s/.test(rest)) {
    rest = rest.replace(/^[-*•]\s*/, "");
  } else {
    rest = `- ${rest}`;
  }
  return ind + rest;
}

/**
 * Apply a per-line transform to the line block covering the selection; returns new text + cursor range.
 * @param {string} fullText
 * @param {number} selStart
 * @param {number} selEnd
 * @param {(line: string) => string} transformLine
 */
export function transformLineBlockSelection(fullText, selStart, selEnd, transformLine) {
  const { blockStart, blockEnd } = getSelectedLineBlockBounds(fullText, selStart, selEnd);
  const block = fullText.slice(blockStart, blockEnd);
  const lines = block.split("\n");
  const nextLines = lines.map(transformLine);
  const newMiddle = nextLines.join("\n");
  const newText = fullText.slice(0, blockStart) + newMiddle + fullText.slice(blockEnd);
  const newSelStart = blockStart;
  const newSelEnd = blockStart + newMiddle.length;
  return { newText, newSelStart, newSelEnd };
}

/**
 * @param {string} fullText
 * @param {number} selStart
 * @param {number} selEnd
 */
export function applyBulletToggleToSelection(fullText, selStart, selEnd) {
  return transformLineBlockSelection(fullText, selStart, selEnd, lineToggleBullet);
}

/**
 * Line = full single line (no trailing \n). Parses leading spaces + optional bullet (- * •).
 * @param {string} line
 * @returns {{ indent: string, bullet: string, body: string }}
 */
function parseLinePrefix(line) {
  const m = /^(\s*)((?:[-*•]\s)?)(.*)$/.exec(line);
  if (!m) return { indent: "", bullet: "", body: line };
  return { indent: m[1], bullet: m[2] || "", body: m[3] };
}

/**
 * Smart Enter: continue list prefix on the next line, or exit an empty list item.
 * Collapsed selection only; Shift+Enter leaves default (plain newline).
 *
 * @param {string} fullText
 * @param {number} selStart
 * @param {number} selEnd
 * @returns {null | { newText: string, newSelStart: number, newSelEnd: number }} null = browser default
 */
export function applySmartEnterAtSelection(fullText, selStart, selEnd) {
  if (selStart !== selEnd) return null;

  const { lineStart, lineEnd } = lineBoundsAt(fullText, selStart);
  const line = fullText.slice(lineStart, lineEnd);
  const { indent, bullet, body } = parseLinePrefix(line);

  if (bullet && !body.trim()) {
    const newText = fullText.slice(0, lineStart) + "\n" + fullText.slice(lineEnd);
    const newPos = lineStart + 1;
    return { newText, newSelStart: newPos, newSelEnd: newPos };
  }

  if (bullet) {
    const insert = `\n${indent}- `;
    const newText = fullText.slice(0, selStart) + insert + fullText.slice(selEnd);
    const newPos = selStart + insert.length;
    return { newText, newSelStart: newPos, newSelEnd: newPos };
  }

  return null;
}
